/**
 * Production-safe logging utility
 * Logs are only output in development mode
 * In production, only errors are logged to console
 */

import { isDevelopment, isProduction } from '../config/env';

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private log(level: LogLevel, message: string, ...args: unknown[]): void {
    // In production, only log errors and warnings
    if (
      isProduction &&
      (level === 'log' || level === 'info' || level === 'debug')
    ) {
      return;
    }

    // In development, log everything
    if (isDevelopment || level === 'error' || level === 'warn') {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

      console[level](prefix, message, ...args);
    }
  }

  /**
   * Log informational messages (development only)
   */
  info(message: string, ...args: unknown[]): void {
    this.log('info', message, ...args);
  }

  /**
   * Log debug messages (development only)
   */
  debug(message: string, ...args: unknown[]): void {
    this.log('debug', message, ...args);
  }

  /**
   * Log warning messages (all environments)
   */
  warn(message: string, ...args: unknown[]): void {
    this.log('warn', message, ...args);
  }

  /**
   * Log error messages (all environments)
   */
  error(message: string, ...args: unknown[]): void {
    this.log('error', message, ...args);
  }

  /**
   * Log general messages (development only)
   */
  logDev(message: string, ...args: unknown[]): void {
    this.log('log', message, ...args);
  }
}

/**
 * Global logger instance
 * Usage:
 * - logger.info('User logged in', { userId: 123 })
 * - logger.error('API call failed', error)
 * - logger.warn('Deprecated feature used')
 * - logger.debug('Debug info', data)
 */
export const logger = new Logger();
